import { Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { Button } from 'reactstrap'
import LoaderButton from 'components/molecules/loader-button'
import { useHistory } from 'react-router-dom'
import * as Yup from 'yup'
import UserAddressFields from 'domains/user/organisms/address-fields'
import UserAddressPinpoint from 'domains/user/organisms/address-pinpoint/user-address-pinpoint'
import pinpointIcon from 'assets/img/current-location.png'
import style from './user-address-form.module.scss'
import { GoogleMap, useLoadScript } from '@react-google-maps/api'

const PLACE_LIBRARIES = [ 'places' ]

const propTypes = {
  handleSubmit : PropTypes.func.isRequired,
  address      : PropTypes.object
}

const validationSchema = Yup.object().shape({
  street_address     : Yup.string().required('Cannot be blank'),
  tl_province_id     : Yup.number().positive('Required').required(),
  tl_city_id         : Yup.number().positive('Required').required(),
  tl_district_id     : Yup.number().positive('Required').required(),
  tl_sub_district_id : Yup.number().positive('Required').required(),
  postal_code        : Yup.string().required('Cannot be blank')
})

/**
 * Form to edit customer address
 * @param {func} handleSubmit Handler for submitting the form
 * @param {object} address initial address
 */
const UserAddressForm = ({
  handleSubmit,
  address
}) => {
  const history = useHistory()

  const [ showAddressFields, setShowAddressFields ] = useState(true)
  const [ showPinpoint, setShowPinpoint ]           = useState(false)

  const { isLoaded } = useLoadScript({
    googleMapsApiKey : process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    // to allow address auto complete in input field
    libraries        : PLACE_LIBRARIES
  })

  const handleClickSetPinpoint = (formikValues) => {
    const provinceName = formikValues.province
    const cityName     = formikValues.city
    const districtName = formikValues.district

    const isLatLongPresent  = formikValues.lat && formikValues.long
    const isLocationNameSet = provinceName && cityName && districtName

    const shouldShowModal = isLatLongPresent || isLocationNameSet

    if (!shouldShowModal) {
      const alertMsg = [
        'Alamat belum lengkap.',
        'Mohon lengkapi alamat sebelum mengatur pinpoint.'
      ].join('\n\n')
      alert(alertMsg)
      return
    }
    setShowAddressFields(false)
    setShowPinpoint(true)
    window.scrollTo({
      top      : 0,
      behavior : 'smooth'
    })
  }

  const handleClosePinpoint = () => {
    setShowAddressFields(true)
    setShowPinpoint(false)
  }

  const showAddressPinpoint = isLoaded && showPinpoint

  return (
    <Formik
      enableReinitialize={ true }
      initialValues={{
        street_address      : address?.street_address ?? '',
        tl_province_id      : address?.tl_province?.id ?? -1,
        tl_city_id          : address?.tl_city?.id ?? -1,
        tl_district_id      : address?.tl_district?.id ?? -1,
        tl_sub_district_id  : address?.tl_sub_district?.id ?? -1,
        postal_code         : address?.postal_code ?? '',
        recipient_name      : address?.recipient_name ?? '',
        telephone           : address?.telephone ?? '',
        alias               : address?.alias ?? '',
        is_customer_default : address?.is_customer_default ?? true,
        is_seller_default   : address?.is_seller_default ?? true,
        lat                 : address.lat ? address.lat : null,
        long                : address.long ? address.long : null,
        note                : address.note || null,
        // for fetching pinpoint center
        province            : address.province ? address.province : '',
        city                : address.city ? address.city : '',
        district            : address.district ? address.district : ''
      }}
      onSubmit={ handleSubmit }
      validationSchema={ validationSchema }
    >
      {
        (formikBag) => {
          const addressCoordinateExist = formikBag.values.lat && formikBag.values.long
          let selectedRegion           = {
            lat : formikBag.values.lat || parseFloat(process.env.REACT_APP_DEFAULT_LATITUDE),
            lng : formikBag.values.long || parseFloat(process.env.REACT_APP_DEFAULT_LONGITUDE)
          }

          return (
            <Form>
              {
                showAddressFields && (
                  <>
                    <UserAddressFields
                      address={ formikBag.values }
                      formikProps={ formikBag }
                    />

                    {
                      // Pinpoint Location display on google maps
                      isLoaded && (
                        <GoogleMap
                          zoom={ 20 }
                          center={ selectedRegion }
                          mapContainerClassName={ `w-100 ${style.mapContainer}` }
                          options={{
                            fullscreenControl : false,
                            streetViewControl : false,
                            mapTypeControl    : false,
                            zoomControl       : false,
                            draggable         : false
                          }}
                        >
                          <div className={ `d-flex flex-column align-items-center ${style.pinpointIcon}` }>
                            {
                              addressCoordinateExist && (
                                <>
                                  <strong className='mb-1 bg-danger p-1 text-white rounded'>
                                    { 'Alamatmu disini' }
                                  </strong>
                                  <img
                                    width={ 27 }
                                    height={ 40 }
                                    src={ pinpointIcon }
                                    alt={ 'current location' }
                                  />
                                </>
                              )
                            }
                          </div>
                          {
                            addressCoordinateExist ? (
                              <Button
                                onClick={ () => handleClickSetPinpoint(formikBag.values) }
                                color='white'
                                className={ `bg-white rounded font-weight-bold shadow ${style.editLocationButton}` }
                              >
                                { 'Ubah Pinpoint' }
                              </Button>
                            ) : (
                              <div className={ style.mapOverlay }>
                                <Button
                                  type='button'
                                  color='white'
                                  className='rounded bg-white shadow font-weight-bold'
                                  onClick={ () => handleClickSetPinpoint(formikBag.values) }
                                >
                                  { 'Pilih titik pinpoint' }
                                </Button>
                              </div>
                            )
                          }
                        </GoogleMap>
                      )
                    }
                  </>
                )
              }

              {
                showAddressPinpoint && (
                  <UserAddressPinpoint
                    provinceName={ formikBag.values.province }
                    cityName={ formikBag.values.city }
                    districtName={ formikBag.values.district }
                    lat={ formikBag.values.lat }
                    long={ formikBag.values.long }
                    setFieldValue={ formikBag.setFieldValue }
                    closePinpoint={ handleClosePinpoint }
                  />
                )
              }

              {
                showAddressFields && (
                  <div className='mt-3 text-right'>
                    <Button
                      color="danger"
                      onClick={ () => history.goBack() }
                    >
                      { 'Back' }
                    </Button>
                    <LoaderButton
                      isLoading={ formikBag.isSubmitting }
                      block={ false }
                      color='success'
                      text="SAVE"
                      type='submit'
                    />
                  </div>
                )
              }
            </Form>
          )
        }
      }
    </Formik>
  )}


UserAddressForm.propTypes = propTypes

export default UserAddressForm